import type {
  AppUpdatePolicy,
  AppUpdatePolicyListResponse,
  CreateContactInquiryRequest,
  CreateContactInquiryResponse,
  CreateItemRequest,
  DeleteAccountResponse,
  Group,
  GroupDetailResponse,
  GroupSummary,
  GroupWithItems,
  Item,
  ItemDetailResponse,
  MeResponse,
  NotificationSettings,
  PurchaseResponse,
  SnoozeResponse,
  UpdateAppUpdatePolicyRequest,
  UpdateItemRequest,
  WaitlistStatus,
} from "@kaidoki/proto";
import * as Sentry from "@sentry/react";
import { getToken, logout } from "../lib/auth";

const REQUEST_TIMEOUT_MS = 5_000;
const API_V0 = "/api/v0";
const API_V1 = "/api/v1";

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<T> {
  const token = getToken();
  if (!token) {
    window.location.href = "/";
    throw new Error("Not authenticated");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(path, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      const error = new NetworkError("timeout");
      Sentry.captureException(error, { extra: { path } });
      throw error;
    }
    const error = new NetworkError("network");
    Sentry.captureException(error, { extra: { path } });
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (res.status === 401) {
    await logout();
    window.location.href = "/";
    throw new Error("Session expired");
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const error = new Error(body.error ?? res.statusText);
    Sentry.captureException(error, {
      extra: { path, status: res.status, body },
    });
    throw error;
  }
  return (await res.json()) as T;
}

export async function getMe(): Promise<MeResponse> {
  return request<MeResponse>(`${API_V0}/auth/me`);
}

export async function listGroupsWithItems(): Promise<GroupWithItems[]> {
  return request<GroupWithItems[]>(`${API_V1}/groups`);
}

export async function createItem(
  groupId: string,
  data: CreateItemRequest,
): Promise<Item> {
  return request<Item>(`${API_V1}/groups/${groupId}/items`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getItem(
  groupId: string,
  id: string,
): Promise<ItemDetailResponse> {
  return request<ItemDetailResponse>(`${API_V1}/groups/${groupId}/items/${id}`);
}

export async function updateItem(
  groupId: string,
  id: string,
  data: UpdateItemRequest,
): Promise<Item> {
  return request<Item>(`${API_V1}/groups/${groupId}/items/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function uploadItemImage(
  groupId: string,
  id: string,
  image: File,
): Promise<Item> {
  return request<Item>(
    `${API_V1}/groups/${groupId}/items/${id}/image`,
    {
      method: "PUT",
      headers: { "Content-Type": image.type },
      body: image,
    },
    15_000,
  );
}

export async function removeItemImage(
  groupId: string,
  id: string,
): Promise<Item> {
  return request<Item>(`${API_V1}/groups/${groupId}/items/${id}/image`, {
    method: "DELETE",
  });
}

export async function deleteItem(groupId: string, id: string): Promise<void> {
  await request<void>(`${API_V1}/groups/${groupId}/items/${id}`, {
    method: "DELETE",
  });
}

export async function purchaseItem(
  groupId: string,
  id: string,
): Promise<PurchaseResponse> {
  return request<PurchaseResponse>(
    `${API_V1}/groups/${groupId}/items/${id}/purchase`,
    { method: "POST" },
  );
}

export async function snoozeItem(
  groupId: string,
  id: string,
  days: number,
): Promise<SnoozeResponse> {
  return request<SnoozeResponse>(
    `${API_V1}/groups/${groupId}/items/${id}/snooze`,
    { method: "POST", body: JSON.stringify({ days }) },
  );
}

export async function createGroup(name: string): Promise<Group> {
  return request<Group>(`${API_V0}/groups`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function getGroupDetail(
  groupId: string,
): Promise<GroupDetailResponse> {
  return request<GroupDetailResponse>(`${API_V0}/groups/${groupId}`);
}

export async function updateGroup(
  groupId: string,
  name: string,
): Promise<Group> {
  return request<Group>(`${API_V0}/groups/${groupId}`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export class AlreadyMemberError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AlreadyMemberError";
  }
}

export async function joinGroup(code: string): Promise<GroupSummary> {
  const token = getToken();
  if (!token) {
    window.location.href = "/";
    throw new Error("Not authenticated");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${API_V0}/groups/join`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ code }),
      signal: controller.signal,
    });
  } catch (err) {
    const apiPath = `${API_V0}/groups/join`;
    if (err instanceof DOMException && err.name === "AbortError") {
      const error = new NetworkError("timeout");
      Sentry.captureException(error, { extra: { path: apiPath } });
      throw error;
    }
    const error = new NetworkError("network");
    Sentry.captureException(error, { extra: { path: apiPath } });
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (res.status === 401) {
    await logout();
    window.location.href = "/";
    throw new Error("Session expired");
  }

  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AlreadyMemberError(body.error ?? "Already a member");
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const error = new Error(body.error ?? res.statusText);
    Sentry.captureException(error, {
      extra: { path: `${API_V0}/groups/join`, status: res.status, body },
    });
    throw error;
  }

  return (await res.json()) as GroupSummary;
}

export async function leaveGroup(groupId: string): Promise<void> {
  await request<void>(`${API_V0}/groups/${groupId}/leave`, {
    method: "DELETE",
  });
}

// Waitlist

export async function getWaitlistStatus(): Promise<WaitlistStatus> {
  return request<WaitlistStatus>(`${API_V0}/waitlist`);
}

export async function joinWaitlist(): Promise<WaitlistStatus> {
  return request<WaitlistStatus>(`${API_V0}/waitlist`, { method: "POST" });
}

export async function leaveWaitlist(): Promise<WaitlistStatus> {
  return request<WaitlistStatus>(`${API_V0}/waitlist`, { method: "DELETE" });
}

export async function getNotificationSettings(): Promise<NotificationSettings> {
  return request<NotificationSettings>(`${API_V0}/notification-settings`);
}

export async function updateNotificationSettings(settings: {
  pushEnabled?: boolean;
  lineEnabled?: boolean;
  emailEnabled?: boolean;
  preferredHours?: number[];
}): Promise<NotificationSettings> {
  return request<NotificationSettings>(`${API_V0}/notification-settings`, {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

// Account deletion
export async function deleteAccount(): Promise<DeleteAccountResponse> {
  return request<DeleteAccountResponse>(`${API_V0}/auth/account`, {
    method: "DELETE",
  });
}

// Contact

export async function submitContactInquiry(
  data: CreateContactInquiryRequest,
): Promise<CreateContactInquiryResponse> {
  return request<CreateContactInquiryResponse>(`${API_V0}/contact`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Admin: app update policies

export async function listAdminAppUpdatePolicies(): Promise<AppUpdatePolicy[]> {
  const response = await request<AppUpdatePolicyListResponse>(
    `${API_V0}/admin/app-update-policies`,
  );
  return response.policies;
}

export async function updateAdminAppUpdatePolicy(
  platform: string,
  data: UpdateAppUpdatePolicyRequest,
): Promise<AppUpdatePolicy> {
  return request<AppUpdatePolicy>(
    `${API_V0}/admin/app-update-policies/${platform}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}
